import { useLayoutEffect, useRef, useState } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';
import { ChevronUp } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';

const positions = new Map();
export function ScrollMemory() {
  const location = useLocation(), navigation = useNavigationType();
  const { user } = useAuth();
  const [shown, setShown] = useState(false);
  const restoring = useRef(false);
  useLayoutEffect(() => {
    const previous = window.history.scrollRestoration;
    window.history.scrollRestoration = 'manual';
    return () => { window.history.scrollRestoration = previous; };
  }, []);
  useLayoutEffect(() => {
    const key = `${user?.id}:${location.key}`;
    const target = navigation === 'POP' ? positions.get(key) || 0 : 0;
    let observer, timer, frame;
    restoring.current = true;
    const remember = () => {
      setShown(window.scrollY > 500);
      if (!restoring.current) {
        positions.set(key, window.scrollY);
        if (positions.size > 200) positions.delete(positions.keys().next().value);
      }
    };
    const finish = () => { restoring.current = false; observer?.disconnect(); clearTimeout(timer); remember(); };
    const restore = () => {
      if (!restoring.current) return;
      window.scrollTo({ top: target, behavior: 'instant' });
      setShown(window.scrollY > 500);
      // Wait for asynchronous card grids / additional search pages to be tall
      // enough instead of accepting the browser's early clamped position.
      if (document.documentElement.scrollHeight - window.innerHeight >= target - 2) {
        clearTimeout(timer); timer = setTimeout(finish, 250);
      }
    };
    const cancel = () => { cancelAnimationFrame(frame); finish(); };
    window.addEventListener('scroll', remember, { passive: true });
    window.addEventListener('wheel', cancel, { passive: true });
    window.addEventListener('touchstart', cancel, { passive: true });
    window.addEventListener('pointerdown', cancel, { passive: true });
    window.addEventListener('keydown', cancel);
    observer = new ResizeObserver(restore); observer.observe(document.querySelector('main') || document.body);
    restore(); frame = requestAnimationFrame(restore);
    const deadline = setTimeout(finish, 15000);
    return () => {
      clearTimeout(deadline); clearTimeout(timer); cancelAnimationFrame(frame); observer.disconnect();
      window.removeEventListener('scroll', remember); window.removeEventListener('wheel', cancel);
      window.removeEventListener('touchstart', cancel); window.removeEventListener('pointerdown', cancel); window.removeEventListener('keydown', cancel);
    };
  }, [location.key, navigation, user?.id]);
  if (!shown) return null;
  return <button type="button" aria-label="Back to top" title="Back to top" onClick={() => window.scrollTo({ top: 0, behavior: window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' })}
    className="fixed bottom-24 right-4 z-30 flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 bg-white/95 text-slate-600 shadow-md hover:bg-emerald-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-emerald-600 lg:bottom-6 lg:right-6"><ChevronUp className="h-5 w-5" /></button>;
}
